/**
 * Cancellation Service
 *
 * Pure functions for calculating cancellation policies and refunds.
 * These functions have no side effects and can be safely tested.
 */

export interface CancellationPolicy {
  refundPercent: number;
  professionalCompensation: number;
  stripeFeeHandling: 'client' | 'platform';
  tier: 1 | 2 | 3;
  tierName: string;
}

export interface RefundCalculation {
  // Input values
  totalAmountCents: number;
  platformFeeCents: number;
  stripeProcessingFeeCents: number;
  professionalNetAmountCents: number;
  hoursUntilMeeting: number;
  cancelledBy: 'client' | 'professional';

  // Calculated values
  policy: CancellationPolicy;
  clientRefundCents: number;
  professionalCompensationCents: number;
  platformRetainsCents: number;

  // Breakdown
  baseRefundCents: number;
  stripeFeeDeductedCents: number;
}

const GRACE_PERIOD_SECONDS = 30; // 30 seconds grace for network delays
const GRACE_PERIOD_HOURS = GRACE_PERIOD_SECONDS / 3600;

/**
 * Calculate cancellation policy based on timing and who cancelled
 */
export function calculateCancellationPolicy(
  hoursUntilMeeting: number,
  cancelledBy: 'client' | 'professional'
): CancellationPolicy {
  // Add grace period for network delays
  const adjustedHours = hoursUntilMeeting + GRACE_PERIOD_HOURS;

  // Professional cancellation: Client always gets 100% refund
  if (cancelledBy === 'professional') {
    return {
      refundPercent: 100,
      professionalCompensation: 0,
      stripeFeeHandling: 'platform', // Platform absorbs Stripe fee to protect client
      tier: 1,
      tierName: 'Professional Cancellation - Full Refund'
    };
  }

  // Client cancellation: Time-based tiers
  if (adjustedHours >= 48) {
    // Tier 1: 48+ hours - Full refund
    return {
      refundPercent: 100,
      professionalCompensation: 0,
      stripeFeeHandling: 'client', // Client pays Stripe fee
      tier: 1,
      tierName: 'Full Refund (48+ hours)'
    };
  } else if (adjustedHours >= 24) {
    // Tier 2: 24-48 hours - Partial refund
    return {
      refundPercent: 50,
      professionalCompensation: 50,
      stripeFeeHandling: 'client', // Client pays Stripe fee
      tier: 2,
      tierName: 'Partial Refund (24-48 hours)'
    };
  } else {
    // Tier 3: <24 hours - No refund, professional gets 100%
    return {
      refundPercent: 0,
      professionalCompensation: 100,
      stripeFeeHandling: 'platform', // Professional keeps all
      tier: 3,
      tierName: 'No Refund (<24 hours)'
    };
  }
}

/**
 * Calculate exact refund amounts based on policy
 */
export function calculateRefundAmounts(
  totalAmountCents: number,
  platformFeeCents: number,
  stripeProcessingFeeCents: number,
  professionalNetAmountCents: number,
  hoursUntilMeeting: number,
  cancelledBy: 'client' | 'professional'
): RefundCalculation {
  const policy = calculateCancellationPolicy(hoursUntilMeeting, cancelledBy);

  let clientRefundCents = 0;
  let professionalCompensationCents = 0;
  let platformRetainsCents = 0;
  let baseRefundCents = 0;
  let stripeFeeDeductedCents = 0;

  if (policy.refundPercent === 100) {
    // Full refund
    baseRefundCents = totalAmountCents;

    if (policy.stripeFeeHandling === 'client') {
      // Client pays Stripe fee
      stripeFeeDeductedCents = stripeProcessingFeeCents;
      clientRefundCents = totalAmountCents - stripeProcessingFeeCents;
    } else {
      // Platform absorbs Stripe fee
      stripeFeeDeductedCents = 0;
      clientRefundCents = totalAmountCents;
    }

    professionalCompensationCents = 0;
    platformRetainsCents = policy.stripeFeeHandling === 'client' ? stripeProcessingFeeCents : (platformFeeCents + stripeProcessingFeeCents);

  } else if (policy.refundPercent === 50) {
    // 50% refund, 50% to professional
    baseRefundCents = Math.round(totalAmountCents * 0.5);
    stripeFeeDeductedCents = stripeProcessingFeeCents; // Full Stripe fee deducted from client
    clientRefundCents = Math.max(0, baseRefundCents - stripeProcessingFeeCents);

    // Professional gets 50% minus their share of platform fee
    const professionalShare = Math.round(totalAmountCents * 0.5);
    const professionalPlatformFee = Math.round(platformFeeCents * 0.5);
    professionalCompensationCents = professionalShare - professionalPlatformFee;

    platformRetainsCents = stripeProcessingFeeCents + professionalPlatformFee;

  } else if (policy.refundPercent === 0) {
    // No refund - professional gets full amount
    baseRefundCents = 0;
    stripeFeeDeductedCents = 0;
    clientRefundCents = 0;
    professionalCompensationCents = professionalNetAmountCents; // Already minus platform fee and Stripe
    platformRetainsCents = platformFeeCents;
  }

  return {
    totalAmountCents,
    platformFeeCents,
    stripeProcessingFeeCents,
    professionalNetAmountCents,
    hoursUntilMeeting,
    cancelledBy,
    policy,
    clientRefundCents,
    professionalCompensationCents,
    platformRetainsCents,
    baseRefundCents,
    stripeFeeDeductedCents
  };
}

/**
 * Calculate hours until meeting from now
 */
export function calculateHoursUntilMeeting(meetingStartTime: Date): number {
  const now = new Date();
  const diffMs = meetingStartTime.getTime() - now.getTime();
  return diffMs / (1000 * 60 * 60);
}

/**
 * Validate that a meeting can be cancelled
 * Returns error message if cannot cancel, null if can cancel
 */
export function validateCancellationEligibility(
  meetingStatus: string,
  meetingStartTime: Date,
  meetingEndTime: Date,
  videoCallStartedAt: Date | null
): string | null {
  const now = new Date();

  // Cannot cancel if already cancelled
  if (meetingStatus === 'cancelled') {
    return 'Meeting is already cancelled';
  }

  // Cannot cancel if completed
  if (meetingStatus === 'completed') {
    return 'Cannot cancel completed meetings. Please contact support to file a dispute.';
  }

  // Cannot cancel if no-show
  if (meetingStatus === 'no_show') {
    return 'Cannot cancel no-show meetings. Please contact support to file a dispute.';
  }

  // Cannot cancel if meeting already started (current time is after start time)
  if (now >= meetingStartTime && now <= meetingEndTime) {
    return 'Cannot cancel during the meeting. If there is an issue, please contact support.';
  }

  // Cannot cancel if meeting already ended
  if (now > meetingEndTime) {
    return 'Cannot cancel past meetings. Please contact support to file a dispute.';
  }

  // Cannot cancel if video call already started
  if (videoCallStartedAt) {
    return 'Cannot cancel: Meeting has started. Please contact support.';
  }

  return null; // Can cancel
}

/**
 * Format refund amount for display
 */
export function formatRefundAmount(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/**
 * Get user-friendly policy description
 */
export function getPolicyDescription(policy: CancellationPolicy, clientRefundCents: number, professionalCompensationCents: number): string {
  const refundDisplay = formatRefundAmount(clientRefundCents);
  const compensationDisplay = formatRefundAmount(professionalCompensationCents);

  if (policy.refundPercent === 100) {
    if (policy.stripeFeeHandling === 'client') {
      return `You will receive a ${refundDisplay} refund (Stripe processing fees are non-refundable). The professional receives no compensation.`;
    } else {
      return `You will receive a full ${refundDisplay} refund. The professional receives no compensation.`;
    }
  } else if (policy.refundPercent === 50) {
    return `You will receive a ${refundDisplay} refund (50% minus Stripe fees). The professional will receive ${compensationDisplay} compensation (50%).`;
  } else {
    return `No refund available. The professional will receive full compensation (${compensationDisplay}).`;
  }
}
